/** @format */

import {Box, ButtonBase, InputBase, MenuItem, Select} from '@mui/material';
import {styled} from '@mui/material/styles';

export const PREFIX = 'HomeOverviewHeader';

export const classes = {
  segmentIcon: `${PREFIX}-segmentIcon`,
  segmentText: `${PREFIX}-segmentText`,
};

export const HeaderRoot = styled(Box)(({theme}) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'flex-end',
  // Wrap controls onto extra rows instead of overflowing into a horizontal page scroll
  // when the viewport is too narrow to fit them in one line.
  flexWrap: 'wrap',
  gap: theme.spacing(4),
  rowGap: theme.spacing(3),
  // No padding here — the parent HomeOverview header (.HomeOverview-header) already applies the
  // 8px/24px header padding; duplicating it here doubled the inset.
  backdropFilter: 'blur(16px)',
}));

export const ControlGroup = styled(Box)(({theme}) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
}));

export const ControlLabel = styled(Box)(({theme}) => ({
  fontSize: 12,
  lineHeight: '14px',
  color: theme.palette.semantic_dark?.surface.onSurfaceLight,
}));

// Segmented toggle container
export const SegmentedToggle = styled(Box)(({theme}) => ({
  display: 'flex',
  flexDirection: 'row',
  gap: '1px',
  borderRadius: 8,
  overflow: 'hidden',
  backgroundColor: theme.palette.semantic_dark?.surface.surface1,
}));

interface SegmentProps {
  isActive: boolean;
}

export const Segment = styled(ButtonBase, {
  shouldForwardProp: prop => prop !== 'isActive',
})<SegmentProps>(({theme, isActive}) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(4),
  cursor: 'pointer',
  backgroundColor: isActive
    ? theme.palette.semantic_dark?.primary.main
    : theme.palette.semantic_dark?.surface.surface1,
  color: isActive
    ? theme.palette.semantic_dark?.primary.on
    : theme.palette.semantic_dark?.surface.onSurfaceLight,
  transition: 'background-color 0.15s, color 0.15s',
  '&:hover': {
    backgroundColor: isActive
      ? theme.palette.semantic_dark?.primary.hover
      : theme.palette.semantic_dark?.surface.surface2,
  },
  [`& .${classes.segmentIcon}`]: {
    fontSize: 16,
    lineHeight: '16px',
  },
  [`& .${classes.segmentText}`]: {
    fontSize: 14,
    lineHeight: '16px',
    whiteSpace: 'nowrap',
  },
}));

// KPI dropdown — styled as a neutral (surface) button: filled surface2, lighter on hover,
// darker on press. The palette has no `neutral` SemanticColor, so the state steps use the
// surface tokens (mirrors the sibling Segment's surface stepping).
export const KpiSelect = styled(Select)(({theme}) => ({
  width: 200,
  height: 48,
  borderRadius: 8,
  backgroundColor: theme.palette.semantic_dark?.surface.surface2,
  color: theme.palette.semantic_dark?.surface.onSurface,
  fontSize: 16,
  padding: 0,
  transition: 'background-color 0.15s',
  '&:hover': {
    backgroundColor: theme.palette.semantic_dark?.surface.surface3,
  },
  '&:active': {
    backgroundColor: theme.palette.semantic_dark?.surface.surface1,
  },
  // Vertically center the selected label inside the 48px button (flex instead of paddings,
  // which left it slightly above the middle). Horizontal padding is kept as-is.
  '& .MuiSelect-select': {
    display: 'flex',
    alignItems: 'center',
    minHeight: 'unset',
    height: '100%',
    boxSizing: 'border-box',
    paddingTop: 0,
    paddingBottom: 0,
    paddingLeft: theme.spacing(4),
    paddingRight: '40px !important',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
}));

export const KpiMenuItem = styled(MenuItem)(({theme}) => ({
  fontSize: 16,
  color: theme.palette.semantic_dark?.surface.onSurface,
}));

// Search input
export const SearchInputRoot = styled(Box)(({theme}) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing(2),
  flexGrow: 1,
  minWidth: 0, // allow the field to shrink below its intrinsic width before the row wraps
  maxWidth: 300,
  height: 48,
  padding: theme.spacing(2),
  borderRadius: 8,
  border: `1px solid ${theme.palette.semantic_dark?.surface.onSurfaceMuted}`,
  backgroundColor: 'transparent',
  boxSizing: 'border-box',
  [`& .${classes.segmentIcon}`]: {
    fontSize: 16,
    flexShrink: 0,
    color: theme.palette.semantic_dark?.surface.onSurfaceLight,
  },
}));

export const SearchInputBase = styled(InputBase)(({theme}) => ({
  flex: 1,
  fontSize: 14,
  color: theme.palette.semantic_dark?.surface.onSurface,
  '& input': {
    padding: 0,
    '&::placeholder': {
      color: theme.palette.semantic_dark?.surface.onSurfaceMuted,
      opacity: 1,
    },
  },
}));

// Clear («close») button inside the search field — appears only when there is a query.
export const SearchClearButton = styled(ButtonBase)(({theme}) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  borderRadius: 4,
  cursor: 'pointer',
  color: theme.palette.semantic_dark?.surface.onSurfaceMuted,
  transition: 'color 0.15s',
  '&:hover': {
    color: theme.palette.semantic_dark?.surface.onSurface,
  },
  // Icon box == the search icon / input line so it sits centered in the field row.
  '& .material-symbols-outlined': {
    fontSize: 16,
    lineHeight: '16px',
  },
}));

// Add project button
export const AddProjectButton = styled(ButtonBase)(({theme}) => ({
  // Splits the header into two groups: the auto left margin absorbs the free space after the
  // filters and pushes the button to the right edge (and keeps it right-aligned when it wraps).
  marginLeft: 'auto',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing(1),
  height: 48,
  padding: theme.spacing(3.5, 4),
  borderRadius: 8,
  backgroundColor: theme.palette.semantic_dark?.primary.main,
  color: theme.palette.semantic_dark?.primary.on,
  fontSize: 14,
  fontWeight: 700,
  lineHeight: '16px',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  userSelect: 'none',
  boxSizing: 'border-box',
  transition: 'background-color 0.15s',
  '&:hover': {
    backgroundColor: theme.palette.semantic_dark?.primary.hover,
  },
  '&.Mui-disabled': {
    backgroundColor: theme.palette.semantic_dark?.disabled.inverseDisabledLow,
    color: theme.palette.semantic_dark?.disabled.inverseDisabled,
    cursor: 'not-allowed',
  },
  [`& .${classes.segmentIcon}`]: {
    fontSize: 16,
  },
}));
